import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API } from "@/App";
import { Calendar, Clock, ArrowRight, BookOpen, Loader2 } from "lucide-react";

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");

  const categories = [
    "All",
    "Investment Basics",
    "Tax Planning",
    "Portfolio Planning",
    "Market Insights"
  ];

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      // First seed the blog posts
      await axios.post(`${API}/seed/blog`).catch(() => {});
      
      const response = await axios.get(`${API}/blog`, {
        params: { category: selectedCategory === "All" ? "" : selectedCategory }
      });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
      // Set sample posts for demo
      setPosts([
        {
          post_id: "1",
          slug: "understanding-sip-power-of-systematic-investing",
          title: "Understanding SIP: The Power of Systematic Investing",
          excerpt: "Learn how SIP can help you build wealth through disciplined, regular investments in mutual funds.",
          category: "Investment Basics",
          author: "Paisa Secure",
          image_url: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800",
          created_at: "2024-01-15T10:00:00Z"
        },
        {
          post_id: "2",
          slug: "tax-saving-elss-complete-guide-2025-26",
          title: "Tax Saving with ELSS: Complete Guide for FY 2025-26",
          excerpt: "Maximize your tax savings under Section 80C with ELSS mutual funds while building long-term wealth.",
          category: "Tax Planning",
          author: "Paisa Secure",
          image_url: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800",
          created_at: "2024-01-10T10:00:00Z"
        },
        {
          post_id: "3",
          slug: "building-first-investment-portfolio",
          title: "Building Your First Investment Portfolio",
          excerpt: "A beginner's guide to creating a diversified mutual fund portfolio based on your goals and risk profile.",
          category: "Portfolio Planning",
          author: "Paisa Secure",
          image_url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
          created_at: "2024-01-05T10:00:00Z"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      <Navbar />
      
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Learning <span className="gold-text">Center</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Expert insights and guides to help you make smarter investment decisions
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat || (cat === "All" && !selectedCategory) ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat === "All" ? "" : cat)}
                className={selectedCategory === cat || (cat === "All" && !selectedCategory) ? "btn-primary" : "border-white/10"}
                data-testid={`category-${cat.toLowerCase().replace(' ', '-')}`}
              >
                {cat}
              </Button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-4">Loading articles...</p>
            </div>
          )}

          {/* Blog Posts Grid */}
          {!loading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.length > 0 ? (
                posts.map((post, index) => (
                  <Link key={post.post_id || index} to={`/blog/${post.slug}`}>
                    <Card className="glass-card card-hover h-full overflow-hidden" data-testid={`blog-card-${index}`}>
                      <div className="aspect-video relative overflow-hidden">
                        <img
                          src={post.image_url || "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800"}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent"></div>
                        <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                          {post.category}
                        </Badge>
                      </div>
                      <CardContent className="p-6">
                        <h2 className="font-serif text-xl font-semibold mb-3 line-clamp-2">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(post.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              5 min read
                            </span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-primary" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">No Articles Found</h3>
                  <p className="text-muted-foreground">Check back soon for new content!</p>
                </div>
              )}
            </div>
          )}

          {/* Newsletter CTA */}
          <Card className="glass-card mt-16">
            <CardContent className="p-8 md:p-12 text-center">
              <h2 className="font-serif text-2xl font-bold mb-4">
                Stay Updated with <span className="gold-text">Market Insights</span>
              </h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Subscribe to our newsletter for weekly investment tips, market analysis, and exclusive content.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-white/10 focus:border-primary focus:outline-none"
                  data-testid="newsletter-email"
                />
                <Button className="btn-primary whitespace-nowrap" data-testid="subscribe-btn">
                  Subscribe
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};
